import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MapPin, Calendar, Mail, Phone } from 'lucide-react';
import SOCIAL_PLATFORMS from '../constants/socialIcons';
import useFavicon from '../hooks/useFavicon';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(str) {
  if (!str) return '';
  return new Date(str).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
}

// ── Photo slideshow (shared by Hero and Showcase layouts) ─────────────────────

function PhotoSlideshow({ photos, height = '100vh', children }) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (photos.length <= 1) return;
    const id = setInterval(() => setIdx(i => (i + 1) % photos.length), 5000);
    return () => clearInterval(id);
  }, [photos.length]);

  return (
    <div style={{ position: 'relative', width: '100%', height, overflow: 'hidden', background: '#111' }}>
      {photos.length > 0 ? (
        photos.map((url, i) => (
          <div key={i} style={{
            position: 'absolute', inset: 0,
            backgroundImage: `url(${url})`,
            backgroundSize: 'cover', backgroundPosition: 'center',
            opacity: i === idx ? 1 : 0,
            transition: 'opacity 1.5s ease',
          }} />
        ))
      ) : (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }} />
      )}
      {/* Gradient overlay for text legibility */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.75) 100%)',
      }} />
      <div style={{ position: 'relative', zIndex: 1, height: '100%' }}>
        {children}
      </div>
    </div>
  );
}

// ── Social links row ──────────────────────────────────────────────────────────

function SocialLinks({ branding, iconSize = 20, gap = 10 }) {
  const links = SOCIAL_PLATFORMS.filter(p => branding[p.key]);
  if (!links.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap }}>
      {links.map(({ key, label, Icon }) => (
        <a key={key} href={branding[key]} target="_blank" rel="noopener noreferrer"
          title={label}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.12)', color: '#fff', textDecoration: 'none', transition: 'background 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
        >
          <Icon size={iconSize} />
        </a>
      ))}
    </div>
  );
}

function SocialLinksLight({ branding, primary, iconSize = 20, gap = 8 }) {
  const links = SOCIAL_PLATFORMS.filter(p => branding[p.key]);
  if (!links.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap }}>
      {links.map(({ key, label, Icon }) => (
        <a key={key} href={branding[key]} target="_blank" rel="noopener noreferrer"
          title={label}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, borderRadius: '50%', background: `${primary}18`, color: primary, textDecoration: 'none', border: `1px solid ${primary}30`, transition: 'background 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.background = `${primary}30`}
          onMouseLeave={e => e.currentTarget.style.background = `${primary}18`}
        >
          <Icon size={iconSize} />
        </a>
      ))}
    </div>
  );
}

// ── Gig list (shared) ─────────────────────────────────────────────────────────

function GigList({ gigs, textColor, mutedColor, accentColor }) {
  if (!gigs.length) return (
    <p style={{ color: mutedColor, fontSize: 14 }}>No upcoming shows confirmed yet.</p>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {gigs.map((g, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flexShrink: 0, width: 6, height: 6, borderRadius: '50%', background: accentColor, marginTop: 7 }} />
          <div>
            <div style={{ fontWeight: 600, color: textColor, fontSize: 14 }}>{g.gig_name}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 3 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: mutedColor }}>
                <Calendar size={11} /> {fmtDate(g.gig_date)}
              </span>
              {g.venue_name && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: mutedColor }}>
                  <MapPin size={11} /> {g.venue_name}{g.venue_location ? `, ${g.venue_location}` : ''}
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Booking info ──────────────────────────────────────────────────────────────

function BookingInfo({ branding, textColor, mutedColor }) {
  if (!branding.show_booking_info) return null;
  if (!branding.booking_email && !branding.booking_phone) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {branding.booking_email && (
        <a href={`mailto:${branding.booking_email}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: textColor, textDecoration: 'none' }}>
          <Mail size={14} color={mutedColor} /> {branding.booking_email}
        </a>
      )}
      {branding.booking_phone && (
        <a href={`tel:${branding.booking_phone}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: textColor, textDecoration: 'none' }}>
          <Phone size={14} color={mutedColor} /> {branding.booking_phone}
        </a>
      )}
    </div>
  );
}

// ── Powered by watermark ──────────────────────────────────────────────────────

function Watermark() {
  return (
    <div style={{
      position: 'fixed', bottom: 14, right: 14, zIndex: 1000,
      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      color: 'rgba(255,255,255,0.75)', padding: '5px 12px', borderRadius: 20,
      fontSize: 11, fontWeight: 500, letterSpacing: 0.2, whiteSpace: 'nowrap',
      userSelect: 'none',
    }}>
      Powered by GigRequest
    </div>
  );
}

// ── LAYOUT: Hero ─────────────────────────────────────────────────────────────

function HeroLayout({ branding, gigs }) {
  const photos = [branding.splash_bg_url, branding.promo_photo_url, branding.header_image_url].filter(Boolean);
  const bg = branding.background_color || '#ffffff';
  const fg = branding.text_color || '#333333';
  const primary = branding.primary_color || '#667eea';
  const muted = `${fg}99`;
  const sectionPad = '48px 24px';
  const maxW = 700;

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', background: bg, minHeight: '100vh' }}>
      {/* Hero */}
      <PhotoSlideshow photos={photos} height="100vh">
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 24px', textAlign: 'center' }}>
          {branding.logo_url && (
            <img src={branding.logo_url} alt={branding.band_name} style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', marginBottom: 20, border: '3px solid rgba(255,255,255,0.3)' }} />
          )}
          <h1 style={{ fontSize: 'clamp(2rem, 8vw, 4.5rem)', fontWeight: 800, color: '#fff', margin: '0 0 12px', textShadow: '0 2px 20px rgba(0,0,0,0.4)', letterSpacing: '-0.02em' }}>
            {branding.band_name || 'Unnamed Band'}
          </h1>
          {branding.tagline && (
            <p style={{ fontSize: 'clamp(1rem, 3vw, 1.35rem)', color: 'rgba(255,255,255,0.85)', margin: 0, textShadow: '0 1px 8px rgba(0,0,0,0.4)', maxWidth: 500 }}>
              {branding.tagline}
            </p>
          )}
          {branding.show_social_media && (
            <div style={{ marginTop: 28 }}>
              <SocialLinks branding={branding} />
            </div>
          )}
        </div>
        <div style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.5)', fontSize: 22, animation: 'gr-bounce 2s infinite' }}>↓</div>
      </PhotoSlideshow>

      {/* Bio */}
      {branding.welcome_message && (
        <section style={{ padding: sectionPad, maxWidth: maxW, margin: '0 auto' }}>
          <h2 style={{ fontSize: 13, fontWeight: 700, color: primary, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 14 }}>About</h2>
          <p style={{ fontSize: 16, lineHeight: 1.75, color: fg, margin: 0, whiteSpace: 'pre-wrap' }}>{branding.welcome_message}</p>
        </section>
      )}

      {/* Upcoming shows */}
      <section style={{ padding: sectionPad, maxWidth: maxW, margin: '0 auto', borderTop: `1px solid ${fg}15` }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: primary, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 18 }}>Upcoming Shows</h2>
        <GigList gigs={gigs} textColor={fg} mutedColor={muted} accentColor={primary} />
      </section>

      {/* Connect */}
      <section style={{ padding: sectionPad, maxWidth: maxW, margin: '0 auto', borderTop: `1px solid ${fg}15` }}>
        <h2 style={{ fontSize: 13, fontWeight: 700, color: primary, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 18 }}>Connect</h2>
        {branding.show_social_media && <div style={{ marginBottom: 20 }}><SocialLinksLight branding={branding} primary={primary} /></div>}
        <BookingInfo branding={branding} textColor={fg} mutedColor={muted} />
      </section>
    </div>
  );
}

// ── LAYOUT: Press Kit ─────────────────────────────────────────────────────────

function PressKitLayout({ branding, gigs }) {
  const bg = branding.background_color || '#ffffff';
  const fg = branding.text_color || '#333333';
  const primary = branding.primary_color || '#667eea';
  const secondary = branding.secondary_color || '#764ba2';
  const muted = `${fg}80`;
  const photo = branding.promo_photo_url || branding.splash_bg_url || branding.header_image_url;

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', background: bg, minHeight: '100vh' }}>
      {/* Header bar */}
      <header style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`, padding: '28px 32px' }}>
        <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          {branding.logo_url && (
            <img src={branding.logo_url} alt={branding.band_name} style={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.4)', flexShrink: 0 }} />
          )}
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 2.2rem)', fontWeight: 800, color: '#fff', margin: 0, letterSpacing: '-0.02em' }}>
              {branding.band_name || 'Unnamed Band'}
            </h1>
            {branding.tagline && <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.8)', margin: '4px 0 0' }}>{branding.tagline}</p>}
          </div>
          {branding.show_social_media && <SocialLinks branding={branding} iconSize={18} />}
        </div>
      </header>

      {/* Two-column body */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 24px', display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,3fr)', gap: 40 }}>
        {/* Left: bio + booking */}
        <div>
          {branding.welcome_message && (
            <div style={{ marginBottom: 32 }}>
              <h2 style={{ fontSize: 11, fontWeight: 700, color: primary, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>About</h2>
              <p style={{ fontSize: 15, lineHeight: 1.7, color: fg, margin: 0, whiteSpace: 'pre-wrap' }}>{branding.welcome_message}</p>
            </div>
          )}
          {(branding.booking_email || branding.booking_phone) && branding.show_booking_info && (
            <div>
              <h2 style={{ fontSize: 11, fontWeight: 700, color: primary, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Bookings</h2>
              <BookingInfo branding={branding} textColor={fg} mutedColor={muted} />
            </div>
          )}
        </div>

        {/* Right: photo + gigs */}
        <div>
          {photo && (
            <div style={{ marginBottom: 28, borderRadius: 12, overflow: 'hidden', aspectRatio: '16/9' }}>
              <img src={photo} alt={branding.band_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
          <h2 style={{ fontSize: 11, fontWeight: 700, color: primary, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 16 }}>Upcoming Shows</h2>
          <GigList gigs={gigs} textColor={fg} mutedColor={muted} accentColor={primary} />
        </div>
      </div>
    </div>
  );
}

// ── LAYOUT: Showcase ──────────────────────────────────────────────────────────

function ShowcaseLayout({ branding, members, gigs }) {
  const photos = [branding.splash_bg_url, branding.promo_photo_url, branding.header_image_url].filter(Boolean);
  const bg = branding.background_color || '#ffffff';
  const fg = branding.text_color || '#333333';
  const primary = branding.primary_color || '#667eea';
  const secondary = branding.secondary_color || '#764ba2';
  const muted = `${fg}80`;
  const surfAlt = `${fg}08`;

  const Section = ({ children, style = {} }) => (
    <section style={{ padding: '64px 24px', maxWidth: 900, margin: '0 auto', ...style }}>
      {children}
    </section>
  );

  const SectionTitle = ({ children }) => (
    <h2 style={{ fontSize: 11, fontWeight: 700, color: primary, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 28, marginTop: 0 }}>
      {children}
    </h2>
  );

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif', background: bg }}>
      {/* Hero — 65vh */}
      <PhotoSlideshow photos={photos} height="65vh">
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', padding: '0 24px 48px', textAlign: 'center' }}>
          {branding.logo_url && (
            <img src={branding.logo_url} alt={branding.band_name} style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', marginBottom: 16, border: '3px solid rgba(255,255,255,0.35)' }} />
          )}
          <h1 style={{ fontSize: 'clamp(2rem, 7vw, 3.8rem)', fontWeight: 800, color: '#fff', margin: '0 0 10px', textShadow: '0 2px 16px rgba(0,0,0,0.5)', letterSpacing: '-0.02em' }}>
            {branding.band_name || 'Unnamed Band'}
          </h1>
          {branding.tagline && (
            <p style={{ fontSize: 'clamp(0.95rem, 2.5vw, 1.2rem)', color: 'rgba(255,255,255,0.8)', margin: 0, textShadow: '0 1px 6px rgba(0,0,0,0.4)' }}>
              {branding.tagline}
            </p>
          )}
        </div>
      </PhotoSlideshow>

      {/* About */}
      {(branding.welcome_message || members.length > 0) && (
        <div style={{ borderBottom: `1px solid ${fg}12` }}>
          <Section>
            <SectionTitle>About</SectionTitle>
            {branding.welcome_message && (
              <p style={{ fontSize: 16, lineHeight: 1.8, color: fg, margin: members.length ? '0 0 40px' : 0, whiteSpace: 'pre-wrap' }}>{branding.welcome_message}</p>
            )}
            {members.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 20 }}>
                {members.map((m, i) => (
                  <div key={i} style={{ textAlign: 'center' }}>
                    {m.photo_url ? (
                      <img src={m.photo_url} alt={m.name} style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', marginBottom: 10 }} />
                    ) : (
                      <div style={{ width: 80, height: 80, borderRadius: '50%', background: `linear-gradient(135deg, ${primary}, ${secondary})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#fff', margin: '0 auto 10px', fontWeight: 700 }}>
                        {m.name?.[0] || '?'}
                      </div>
                    )}
                    <div style={{ fontWeight: 600, fontSize: 14, color: fg }}>{m.name}</div>
                    {m.role && <div style={{ fontSize: 12, color: muted, marginTop: 2 }}>{m.role}</div>}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}

      {/* Upcoming Shows */}
      <div style={{ background: surfAlt, borderBottom: `1px solid ${fg}12` }}>
        <Section>
          <SectionTitle>Upcoming Shows</SectionTitle>
          {gigs.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
              {gigs.map((g, i) => (
                <div key={i} style={{ background: bg, borderRadius: 10, padding: '18px 20px', border: `1px solid ${fg}12` }}>
                  <div style={{ fontSize: 12, color: primary, fontWeight: 700, marginBottom: 6 }}>
                    <Calendar size={11} style={{ marginRight: 5 }} />{fmtDate(g.gig_date)}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: fg, marginBottom: 4 }}>{g.gig_name}</div>
                  {g.venue_name && (
                    <div style={{ fontSize: 13, color: muted, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={11} /> {g.venue_name}{g.venue_location ? `, ${g.venue_location}` : ''}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: muted, fontSize: 15, margin: 0 }}>No upcoming shows confirmed yet.</p>
          )}
        </Section>
      </div>

      {/* Connect */}
      <Section>
        <SectionTitle>Connect</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          {branding.show_social_media && <SocialLinksLight branding={branding} primary={primary} iconSize={22} gap={12} />}
          <BookingInfo branding={branding} textColor={fg} mutedColor={muted} />
        </div>
      </Section>
    </div>
  );
}

// ── Loading / Not Found ───────────────────────────────────────────────────────

function NotFound() {
  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, sans-serif', background: '#0f0f0f', color: '#888', gap: 12 }}>
      <div style={{ fontSize: 48 }}>🎸</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: '#ccc' }}>Band not found</div>
      <div style={{ fontSize: 14 }}>That link might be outdated or the band hasn't set up their page yet.</div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function BandPublicPage() {
  const { bandSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/band/${bandSlug}/public`)
      .then(res => setData(res.data))
      .catch(err => { if (err.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [bandSlug]);

  if (loading) return (
    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f0f0f' }}>
      <Loader2 size={32} color="#888" style={{ animation: 'gr-spin 0.8s linear infinite' }} />
    </div>
  );
  if (notFound || !data) return <NotFound />;

  const { branding, members, gigs } = data;
  const layout = branding.public_page_layout || 'hero';

  return <BandPublicPageContent branding={branding} members={members} gigs={gigs} layout={layout} />;
}

function BandPublicPageContent({ branding, members, gigs, layout }) {
  useFavicon(branding.logo_url);

  return (
    <>
      {layout === 'hero'     && <HeroLayout     branding={branding} members={members} gigs={gigs} />}
      {layout === 'presskit' && <PressKitLayout  branding={branding} members={members} gigs={gigs} />}
      {layout === 'showcase' && <ShowcaseLayout  branding={branding} members={members} gigs={gigs} />}
      {branding.subscription_tier === 'free' && <Watermark />}
    </>
  );
}
